import React, { useCallback, useContext, useEffect } from 'react';
import { renderRoutes } from 'react-router-config';
import { useTranslation } from 'react-i18next';
import { PapiUser } from '@wix/da-papi-types';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { Url, USER_PROFILE_SHOP_SUB_PATHS } from '@wix/da-url';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import SubSubNavBar from '../../SubSubNavBar';
import ShopAll from './ShopAll';
import ShopCommissions from './ShopCommissions';
import ShopPrints from './ShopPrints';
import ShopPremiumContent from './ShopPremiumContent';
import ShopPremiumFolders from './ShopPremiumFolders';

import s from './ShopSection.scss';

export interface Props {
  profileOwnerUser: PapiUser;
  isOwner: boolean;
  isLoggedIn: boolean;
  isVerified: boolean;
  total: {
    all: number;
    commissions: number;
    premiumContent: number;
    premiumFolders: number;
    prints: number;
  };
  restrictLoggedOut(): void;
  openCommissionModal(): void;
}

const ShopSection: React.FC<Props> = ({
  profileOwnerUser,
  isLoggedIn,
  isVerified,
  isOwner,
  total,
  restrictLoggedOut,
  openCommissionModal,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const kill_prints_act_one = useFlag('kill_prints_act_one');

  useEffect(() => {
    if (window.location.search.includes('new_commission')) {
      if (!isLoggedIn) {
        restrictLoggedOut();
      }
      if (isVerified && isOwner) {
        openCommissionModal();
      }
    }
  });

  const getShopRoutes = useCallback(() => {
    const { username: ownerUsername } = profileOwnerUser;
    const username = ownerUsername.toLowerCase();
    const showPrints = total.prints > 0 || !kill_prints_act_one;
    return [
      {
        label: t('common.all'),
        count: total.all,
        path: Url.userShopPath(username, USER_PROFILE_SHOP_SUB_PATHS.ALL),
        component: ShopAll,
        exact: true,
        bi_item_label: 'all',
      },
      {
        label: t('common.premiumFolders'),
        count: total.premiumFolders,
        path: Url.userShopPath(
          username,
          USER_PROFILE_SHOP_SUB_PATHS.PREMIUM_FOLDERS
        ),
        component: ShopPremiumFolders,
        bi_item_label: USER_PROFILE_SHOP_SUB_PATHS.PREMIUM_FOLDERS,
      },
      {
        label: t('common.commissions_other'),
        count: total.commissions,
        path: Url.userShopPath(
          username,
          USER_PROFILE_SHOP_SUB_PATHS.COMMISSIONS
        ),
        component: ShopCommissions,
        bi_item_label: USER_PROFILE_SHOP_SUB_PATHS.COMMISSIONS,
      },
      showPrints && {
        label: t('common.prints_other'),
        count: total.prints,
        path: Url.userShopPath(username, USER_PROFILE_SHOP_SUB_PATHS.PRINTS),
        component: ShopPrints,
        bi_item_label: USER_PROFILE_SHOP_SUB_PATHS.PRINTS,
      },
      {
        label: t('common.premiumContent'),
        count: total.premiumContent,
        path: Url.userShopPath(
          username,
          USER_PROFILE_SHOP_SUB_PATHS.PREMIUM_CONTENT
        ),
        component: ShopPremiumContent,
        bi_item_label: USER_PROFILE_SHOP_SUB_PATHS.PREMIUM_CONTENT,
      },
    ].filter(i => !!i);
  }, [t, profileOwnerUser, kill_prints_act_one, total]);

  return (
    <div className={s['root']}>
      {!isMobile ? (
        <SubSubNavBar className={s['section-nav']} items={getShopRoutes()} />
      ) : null}
      <div>{renderRoutes(getShopRoutes())}</div>
    </div>
  );
};
ShopSection.displayName = 'ShopSection';

export default ShopSection;
